import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useContext, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

import styled from "styled-components/macro";

import { FlocknoteAppState } from "Flocknote/state";

import { faFileSpreadsheet, faPrint } from "@fortawesome/pro-duotone-svg-icons";
import qs from "qs";

import Portal from "Utilities/PortalMaker";

import { usePaymentTypeVerbiage } from "../../Utilities/Hooks/usePaymentTypeVerbiage";

import API from "./API";
import RecurringGiftAccordion from "./RecurringGiftAccordion";
import mockData from "./mockData";

const USE_MOCK_DATA = false;

export default function RecurringGivingList() {
  const headerRef = useRef();

  const [recurringGifts, setRecurringGifts] = useState({});
  const [expectedNextMonth, setExpectedNextMonth] = useState();
  const [remainingThisMonth, setRemainingThisMonth] = useState();
  const [paymentType, setPaymentType] = useState();
  const [hasRecurringOn, setHasRecurringOn] = useState();

  const { nounCap, noun } = usePaymentTypeVerbiage(paymentType);

  const [
    {
      networkGiving: { refreshRecurringGiftsList },
    },
  ] = useContext(FlocknoteAppState);

  const location = useLocation();
  const {
    fund: fundID,
    campaignID,
    batch,
  } = qs.parse(location.search, {
    ignoreQueryPrefix: true,
  });

  // closed-height is for calculating max-height of each recurring gift group for mobile screens
  const recurringGiftsSectionsCount = Object.keys(recurringGifts).length;
  const closedHeight =
    recurringGifts && headerRef?.current?.clientHeight
      ? headerRef?.current?.clientHeight + recurringGiftsSectionsCount * 44.5
      : 0;

  const isBatchInputPage = location.pathname.includes("/giving/input");

  useEffect(
    function getInitialData() {
      if (campaignID || batch || isBatchInputPage) return;

      if (USE_MOCK_DATA) {
        setHasRecurringOn(true);
        setRecurringGifts(mockData);
        return;
      }

      if (fundID) {
        API.getRecurringGifts({ fundID })
          .then((res) => {
            // console.log("res", res);
            setRecurringGifts({
              active: res.active,
              paused: res.paused,
              stopped: res.stopped,
              ended: res.ended,
            });
            setExpectedNextMonth(res.expectedPaymentsNextMonth);
            setRemainingThisMonth(res.paymentsLeftThisMonth);
            setPaymentType(res.paymentType);
            setHasRecurringOn(res.hasRecurringOn);
          })
          .catch((err) => {
            console.log("error", err);
          });
      }

      return () => setRecurringGifts({});
    },
    [campaignID, batch, isBatchInputPage, refreshRecurringGiftsList, fundID]
  );

  const givingColumnRight = document.querySelector(
    ".network_giving_column_right .network_giving_column_inside"
  );

  // don't try to show on giving page, campaign page, on batch input page or batch page,
  // if recurring is off for the fund, or if the right column just doesn't exist
  if (
    campaignID ||
    !fundID ||
    isBatchInputPage ||
    batch ||
    !hasRecurringOn ||
    !givingColumnRight
  ) {
    return null;
  }

  return (
    <Portal to={givingColumnRight}>
      <Wrapper className={recurringGifts?.length === 0 && "loading"}>
        <Header ref={headerRef}>
          <div>
            <TitleRow>
              <Title>Recurring {nounCap}s</Title>
              <IconsWrapper>
                <a
                  href={`/giving/printReport?report=recurringPayments&fundID=${fundID}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  type="application/pdf"
                >
                  <IconBox className="faPrint" side="left">
                    <FontAwesomeIcon icon={faPrint} />
                  </IconBox>
                </a>

                <a
                  href={`/giving/export?download=Recurring_Payments_Export&report=recurringPayments&fundID=${fundID}`}
                  download={true}
                  type="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                >
                  <IconBox className="faFileSpreadSheet" side="right">
                    <FontAwesomeIcon icon={faFileSpreadsheet} />
                  </IconBox>
                </a>
              </IconsWrapper>
            </TitleRow>
            <Description>
              Below is a list of recurring {noun}s for this fund. Click on one
              to open their giving profile.
            </Description>
          </div>

          {Object.keys(recurringGifts)?.length > 0 ? (
            <>
              <SummaryInfo data-cy="recurringRemainingThisMonth">
                Remaining {noun}s this month:{" "}
                <Amount>{remainingThisMonth}</Amount>
              </SummaryInfo>
              <SummaryInfo data-cy="recurringExpectedNextMonth">
                Total expected next month: <Amount>{expectedNextMonth}</Amount>
              </SummaryInfo>
            </>
          ) : (
            <SummaryInfo>There are no recurring gifts yet.</SummaryInfo>
          )}
        </Header>

        <RecurringGiftAccordion
          recurringGifts={recurringGifts}
          closedHeight={closedHeight}
          paymentTypeNoun={noun}
        />
      </Wrapper>
    </Portal>
  );
}

const Wrapper = styled.div`
  border-left: 1px solid #d4d9db;
  height: 100%;
  box-sizing: border-box;
  transition: transform 0.4s, opacity 0.4s;
  display: flex;
  flex-direction: column;
  height: 100vh;

  .loading &,
  &.loading {
    transform: translateX(100%);
    opacity: 0;
  }

  @media screen and (max-width: 1200px) and (min-width: 841px),
    screen and (max-width: 720px) {
    background: #fff;
    border-radius: 10px;
    max-width: 620px;
    margin: 0 auto;
    margin-bottom: 120px;
    border: 1px solid #d4d9db;
    overflow: hidden;
    /* height: auto;
    max-height: 100vh; */
  }

  @media screen and (max-width: 1200px) and (min-width: 841px) {
    height: auto;
  }

  @media screen and (max-width: 720px) {
    /* height: 100vh; */
    height: auto;
    max-height: 100vh;
  }

  @media screen and (min-width: 1521px) {
    margin-top: 10px;
  }
`;

const Header = styled.div`
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 12px;

  @media screen and (min-width: 1521px) {
    flex-grow: 0;
    flex-shrink: 0;
  }
`;

const TitleRow = styled.div`
  display: flex;
  justify-content: space-between;
  /* align-items: flex-start; */
`;

const Title = styled.div`
  font-size: 20px;
  font-weight: 400;
  margin-bottom: 6px;
`;

const IconsWrapper = styled.div`
  /* background: hsl(0 0% 70%); */
  border-radius: 6px;
  display: flex;
  gap: 0.5px;

  & > a:first-child > div {
    border-top-left-radius: 9999px;
    border-bottom-left-radius: 9999px;
  }
  & > a:last-child > div {
    border-top-right-radius: 9999px;
    border-bottom-right-radius: 9999px;
  }
`;

const IconBox = styled.div`
  --fa-secondary-opacity: 1;
  --fa-primary-opacity: 0.6;

  color: #fff;
  font-size: 17px;
  cursor: pointer;
  transition: box-shadow 0.2s;
  width: 31px;
  height: 31px;
  background: linear-gradient(
    to bottom,
    #b4bbc1,
    #a7afb4,
    #9aa3a7,
    #8e989a,
    #828c8d
  );
  text-align: center;
  line-height: 31px;
  border-top-left-radius: ${(props) => (props.side === "left" ? "7px" : 0)};
  border-bottom-left-radius: ${(props) => (props.side === "left" ? "7px" : 0)};
  border-bottom-right-radius: ${(props) =>
    props.side === "right" ? "7px" : 0};
  border-top-right-radius: ${(props) => (props.side === "right" ? "7px" : 0)};
  position: relative;

  &.faPrint {
    --fa-secondary-opacity: 0.6;
    --fa-primary-opacity: 1;
  }

  svg {
    scale: 1;
    transition: scale 0.2s ease;
  }

  &:hover svg {
    scale: 1.1;
  }

  :before {
    content: "";
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    background-color: hsla(0 0% 0% / 0);
    transition: background-color 0.2s ease;
  }

  &:hover::before {
    background-color: hsla(0 0% 0% / 0.12);
    border-top-left-radius: ${(props) => (props.side === "left" ? "7px" : 0)};
    border-bottom-left-radius: ${(props) =>
      props.side === "left" ? "7px" : 0};
    border-bottom-right-radius: ${(props) =>
      props.side === "right" ? "7px" : 0};
    border-top-right-radius: ${(props) => (props.side === "right" ? "7px" : 0)};
  }
`;

const Description = styled.div`
  font-size: 14px;
  color: #828c8d;
`;

const SummaryInfo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 14px;
  font-weight: 400;
`;

const Amount = styled.div`
  font-size: 16px;
  font-weight: 600;
`;
